import { DestroyRef, inject, Injectable, NgZone } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { map, mergeMap, takeUntil } from "rxjs";

import { GlossScrollbar, glossScrollEnhanced, UNSCROLLABLE } from "./gloss-scrollbar";

// The glass confirmation dialogs keep their always-visible native bar (#4614, material-ui.scss):
// some can't be dismissed, and a hidden bar could hide the only way out below the fold.
export const KEEPS_NATIVE_BAR = /\bconfirmation-dialog--(error|warning|info)\b/;

/**
 * GlossScroll's overlay bar on every MatDialog's surface, which is the scroll container Material
 * creates for the dialog (not in any of our templates). app-wide, the surfaces' native bar is
 * hidden (material-ui.scss), so this is their only scroll indicator. Started once by AppComponent;
 * Material reports child injectors' dialogs to the root dialog's afterOpened too.
 */
@Injectable({ providedIn: "root" })
export class GlossScrollDialogsService {
  private readonly zone = inject(NgZone);

  constructor() {
    if (!glossScrollEnhanced()) {
      return;
    }
    // MatDialog reports a dialog before its container's first change detection, which is what
    // gives the container its id; by the time the dialog itself has opened, it has one. One
    // closed while still opening never opens.
    const subscription = inject(MatDialog)
      .afterOpened.pipe(
        mergeMap((ref) =>
          ref.afterOpened().pipe(
            map(() => ref),
            takeUntil(ref.afterClosed()),
          ),
        ),
      )
      .subscribe((ref) => this.attach(ref));
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
  }

  private attach(ref: MatDialogRef<unknown>): void {
    const container = document.getElementById(ref.id);
    const surface = container?.querySelector<HTMLElement>(".mat-mdc-dialog-surface");
    const pane = container?.closest(".cdk-overlay-pane");
    if (!surface || KEEPS_NATIVE_BAR.test(pane?.className ?? "")) {
      return;
    }
    // Most surfaces never scroll (material-ui.scss: overflow-y: visible); their content does.
    if (UNSCROLLABLE.test(getComputedStyle(surface).overflowY)) {
      return;
    }
    const scrollbar = this.zone.runOutsideAngular(
      () => new GlossScrollbar(surface, { enhanced: true }),
    );
    ref.afterClosed().subscribe(() => scrollbar.destroy());
  }
}
